import moment from 'moment';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import IconToolTip from '../../../common/IconToolTip';
import Loader from '../../../common/Loader';
import MiniPopup from '../../../common/MiniPopup';
import { validateStringValue } from '../../../utils/common';
import { LineClampComponent } from '../TicketTableComponent';
import ViewAllRemark from '../Modal/ViewAllRemark';
import { closeModalAction, openCustomModal } from '../../../actions/modal';
import ButtonScogoOrange from '../../../common/Buttons/ButtonScogoOrange';
import { AddIssueCategory } from './AddIssueCategory';
import { AttributeKeys, canEditTicket } from '../TicketController';
import { ticket } from '../../../router/roleWisePageRoute';

export const RemarkColumn = ({ ticket_id, loading }) => {
    const serviceRequestList = useSelector((state) => state.serviceRequest.serviceRequestList);
    const serviceRequest = useMemo(() => {
        return serviceRequestList.data.find((item) => item.id === ticket_id);
    }, [serviceRequestList, ticket_id]);

    const dispatch = useDispatch();

    if (loading) {
        return (
            <div className='relative w-3/12 flex justify-center items-center h-10'>
                <Loader color={'#F48A21'} size='15' speed='1' thickness='2' />
            </div>
        );
    }

    const closeModal = () => {
        dispatch(closeModalAction());
    };

    const viewRemarks = () => {
        let modalComponent = <ViewAllRemark closeModal={closeModal} ticketId={ticket_id} source='ticket_table' remarkList={serviceRequest?.remarks} />;
        let modalWidth = '60em',
            modalHeight = '70vh';
        let head = (
            <div className='flex items-center'>
                <span className='text-black'>
                    Remarks:&nbsp;
                    <span className='text-scogobgsky'>
                        {serviceRequest.ticket_number}
                    </span>
                </span>
            </div>
        );
        dispatch(openCustomModal({ modalComponent, heading: head, modalWidth, modalHeight }));
    };

    return (
        <motion.div
            className='w-full'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
            <motion.div
                className='flex flex-col space-y-2'
            >
                <motion.div
                    whileHover={{ color: '#F48A21' }}
                    className='transition-colors duration-200'
                >

                    {serviceRequest?.remarks?.slice(0, 2)?.map((remark) => {
                        let someLongText = '';
                        if (validateStringValue(remark?.remark)) {
                            someLongText = remark?.remark
                        } else if (remark?.event) {
                            someLongText = remark?.event
                        }
                        if (!someLongText) return null;
                        return (
                            <Remark
                                remarkText={someLongText}
                                remarkBy={`${validateStringValue(remark?.created_by?.first_name)} ${validateStringValue(remark?.created_by?.last_name)}`}
                                remarkDate={moment(remark.created_at).format('DD/MM/YYYY')}
                                key={remark.created_at}
                            />
                        );
                    })}

                    <IconToolTip title={'View All Remarks'}>
                        <div
                            onClick={viewRemarks}
                            className='max-w-max text-scogobgsky cursor-pointer text-left text-font10 hover:text-scogoorange flex items-center gap-1'
                        >
                            <span className="material-icons text-font11 pt-1">comment</span>
                            <span>Add/View Remarks</span>
                        </div>
                    </IconToolTip>
                </motion.div>
                <AddIssue serviceRequest={serviceRequest} />
            </motion.div>
        </motion.div>
    );
};

const Remark = ({ remarkBy, remarkText, remarkDate }) => {
    return (
        <motion.div
            className=''
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
        >
            <p className='td_p_light_primary md:mt-2 mt-0 md:mr-0 mr-2 md:hidden block text-sm font-medium'>Remarks:</p>
            <LineClampComponent value={`${remarkText ? remarkText : ''}`} lineClamp='line-clamp-2' />
            <div className='md:block flex flex-wrap items-center mt-1'>
                <p className='text-sm text-scogogray font-normal md:mr-0 mr-2 italic'>{`${remarkBy} on ${remarkDate}`}</p>
            </div>
        </motion.div>
    );
};

export const ViewAllRemarks = ({ remarkList, displayName, onHovertitle, positionClass = 'lg:top-6 top-4' }) => {
    if (!displayName.trim()) return <></>;
    const Details = () => {
        return (
            <div className='flex flex-col gap-4 w-20vw z-50 p-2'>
                {remarkList?.map((remark, index) => {
                    let someLongText = (remark?.remark?.length > 0 ? remark?.remark : '') + (remark?.event?.length > 0 ? remark?.event : '');

                    return (
                        remark && (
                            <Remark
                                key={remark.created_at}
                                remarkText={someLongText}
                                remarkBy={`${validateStringValue(remark?.created_by?.first_name)} ${validateStringValue(remark?.created_by?.last_name)}`}
                                remarkDate={moment(remark.created_at).format('DD/MM/YYYY')}
                            />
                        )
                    );
                })}
            </div>
        );
    };
    return (
        <div className='w-full relative cursor-pointer flex items-center text-scogobgsky font-medium gap-3 max-h-50vh'>
            <MiniPopup
                handleClick={() => { }}
                popup={Details()}
                position='ml-auto w-full'
                className={`absolute px-3 py-2 mt-2 bg-white max-h-50vh text-black overflow-scroll rounded-md shadow-xl z-50 ${positionClass}`}
                onHover=''
            >
                {displayName?.trim() && <div className='flex items-start w-full'></div>}
            </MiniPopup>
        </div>
    );
};

const AddIssue = ({ serviceRequest }) => {
    const showAddIssue = canEditTicket(serviceRequest);
    const issue = useMemo(() => {
        let i = '';
        if (serviceRequest && serviceRequest.attributes) {
            if (serviceRequest.attributes && Array.isArray(serviceRequest.attributes)) {
                i = serviceRequest.attributes.find((item) => item.key === AttributeKeys.issue_category);
            }
        }
        return i;
    }, [serviceRequest]);

    const dispatch = useDispatch();

    const addIssueCategory = () => {
        const heading = issue ? 'Edit Issue' : 'Add Issue';
        dispatch(openCustomModal({
            heading: heading,
            modalWidth: '40vw',
            modalHeight: 'auto',
            modalComponent: <AddIssueCategory ticket_id={serviceRequest.id} org_id={serviceRequest.org_id} />
        }));
    }

    if (!issue && showAddIssue) {
        return (
            <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <ButtonScogoOrange
                    buttonClass='text-sm flex items-center gap-1 px-3 rounded-full shadow-sm hover:shadow-md transition-all duration-200'
                    textOrComponent={
                        <div className="flex items-center">
                            <span className="material-icons text-sm mr-1">add_circle</span>
                            <span>Add Issue</span>
                        </div>
                    }
                    onClick={addIssueCategory}
                />
            </motion.div>
        );
    }

    return (
        <motion.div className='flex items-center'>

            {issue?.value?.str && <div className='px-3 flex gap-2 items-center rounded-full w-fit max-w-[80%] border border-scogoddd bg-gray-50'>
                <IconToolTip title={`Issue category: ${issue?.value?.str}`}>
                    <div className='flex items-center truncate'>
                        <span className="material-icons text-scogobgsky text-sm mr-1">label</span>
                        <span className='text-sm text-black font-medium truncate'>{issue?.value?.str}</span>
                    </div>
                </IconToolTip>
            </div>}
            {showAddIssue ?
                <>
                    <span className='text-gray-300 pl-2 pr-1'>|</span>
                    <motion.span
                        className='material-icons text-sm text-scogobgsky cursor-pointer'
                        onClick={addIssueCategory}
                        whileHover={{ color: '#F48A21', scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        edit
                    </motion.span>
                </>
                : null}
        </motion.div>
    );
}